import { call, put, select } from 'redux-saga/effects';
import { IAgendaItem } from '../../../resource/agenda-item/agenda-item.types';
import { IMeeting } from '../../../resource/meeting/meeting.types';
import * as actions from './agenda-items.actions';

import * as meetingService from '../../../resource/meeting/meeting.saga-service';
import * as agendaService from '../../../resource/agenda-item/agenda-item.saga-service';

import { DropResult } from 'react-beautiful-dnd';
import { agendaItemsPageSelectors } from './agenda-items.selectors';
import { meetingSelectors } from '../../../resource/meeting/meeting.selectors';
import { IAgendaItems, ICurrentAgendaIndex } from './agenda-items.types';
import { sessionSelectors } from '../../../resource/session/session.selectors';
import { ISession } from '../../../resource/session/session.types';
import * as sessionService from '../../../resource/session/session.saga-service';
import { IMeetingSimple } from '../../../resource/meeting-simple/meeting-simple.types';
import { closeDialog } from '../../../dialog/dialog.actions';
import { selectWithParams } from '../../../../utils/selector.utils';
import { agendaItemSelectors } from '../../../resource/agenda-item/agenda-item.selectors';
import { setupMeetingBoards } from '../meeting-boards/meeting-boards.saga-services';
import { setupMeetingTasks } from '../meeting-tasks/meeting-tasks.saga-services';
import { updateMeetingDurationFromAgenda } from '../../../resource/meeting-simple/meeting-simple.saga-service';
import { IUser } from '../../../resource/user/user.types';
import { authSelectors } from '../../../auth/auth.selectors';

export function* storeAgendaItems(agendaItems?: IAgendaItem['id'][]) {
  //yield agendaItems ? put(actions.storeAgendaItems(agendaItems)) : put(actions.clearAgendaItems());
}

export function* getAgendaItems(meetingId?: string) {
  const agendaItemsIds = (yield select(agendaItemsPageSelectors.selectAgendaItems)) as IAgendaItems;
  const newAgendaItemsIds = { ...agendaItemsIds };

  if (meetingId && agendaItemsIds[meetingId]) {
    const agendaItems = (yield call(agendaService.getAgendaItems, meetingId)) as IAgendaItem[];
    newAgendaItemsIds[meetingId] = agendaItems.map((ag) => ag.id);
  } else {
    const agendaItems = (yield call(agendaService.getAgendaItems)) as IAgendaItem[];
    for (const agendaItem of agendaItems) {
      if (!newAgendaItemsIds[agendaItem.meetingId]) {
        newAgendaItemsIds[agendaItem.meetingId] = [];
      }
      if (!newAgendaItemsIds[agendaItem.meetingId].includes(agendaItem.id)) {
        newAgendaItemsIds[agendaItem.meetingId].push(agendaItem.id);
      }
    }
  }
  yield put(actions.storeAgendaItems(newAgendaItemsIds));
}

export function* setupAgendaItemsForMeetingForm(agendaItemIds: IAgendaItem['id'][]) {
  yield call(storeAgendaItems, agendaItemIds);
}

export function* setupAgendaItemsForMeeting(meetingId: IMeeting['id']) {
  const meeting = (yield select(meetingSelectors.selectResourceById, meetingId)) as IMeeting;

  // // const meeting = (yield call(meetingService.populateAgendaItems, meetingId)) as IMeeting;

  if (meeting && meeting.agendaItemIds) {
    yield call(storeAgendaItems, meeting.agendaItemIds);
  }
  yield call(meetingService.searchMeetings, { type: meeting.type });
}

export function* saveAgendaItems(meetingId: IMeetingSimple['id'], agendaItems: IAgendaItem[]) {
  const response = yield call(agendaService.saveAgendaItems, meetingId, agendaItems);
  const agendaItemsIds = (yield select(agendaItemsPageSelectors.selectAgendaItems)) as IAgendaItems;
  const newAgendaItemsIds = { ...agendaItemsIds };
  newAgendaItemsIds[meetingId] = response.map((ag: IAgendaItem) => ag.id);
  yield put(actions.storeAgendaItems(newAgendaItemsIds));
  yield put(closeDialog('agendaFormDialog'));
  yield put(closeDialog('agendaCustomFormDialog'));
}

export function* saveAgendaItemsSocket(
  meetingId: IMeetingSimple['id'],
  agendaItems: IAgendaItem[],
  actionUserId: number,
) {
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  if (String(actionUserId) == loggedUserId) {
    return;
  }

  yield call(agendaService.storeAgendaItem, agendaItems);
  const agendaItemsIds = (yield select(agendaItemsPageSelectors.selectAgendaItems)) as IAgendaItems;
  const newAgendaItemsIds = { ...agendaItemsIds };
  newAgendaItemsIds[meetingId] = agendaItems.map((ag: IAgendaItem) => ag.id);
  yield put(actions.storeAgendaItems(newAgendaItemsIds));
  // now update the duration of the meeting in the store
  yield call(updateMeetingDurationFromAgenda, meetingId, agendaItems);
}

export function* changeOrder(meetingId: IMeeting['id'], result: DropResult) {
  if (!result.destination) {
    return;
  }

  const sourceIndex = result.source.index;
  const destinationIndex = result.destination.index;
  const agendaItemId = result.draggableId;

  const agendaItemsIds = (yield select(agendaItemsPageSelectors.selectAgendaItems)) as IAgendaItems;
  const agendaItems = agendaItemsIds[meetingId];
  agendaItems.splice(sourceIndex, 1);
  agendaItems.splice(destinationIndex, 0, agendaItemId);
  const agendaItemsRes = yield selectWithParams(agendaItemSelectors.selectResourcesById, agendaItems);

  const agendaItemsSorted = agendaItemsRes.map((ai: IAgendaItem, index: number) => ({
    ...ai,
    sort: `${index}`,
  }));
  yield put(actions.saveAgendaItems(meetingId, agendaItemsSorted));
}

export function* clearAgendaItemsPageStorage() {
  yield put(actions.clearAgendaItems());
  yield call(actions.clearCurrentAgenda);
}

export function* storeNextAgendaIndex(currentAgendaIndex: ICurrentAgendaIndex) {
  yield call(agendaService.moveStepAgenda, currentAgendaIndex);
}

export function* moveStepAgenda(currentAgendaIndex: ICurrentAgendaIndex) {
  yield call(agendaService.moveStepAgenda, currentAgendaIndex);
}

export function* storeCurrentAgenda(currentAgendaIndex: ICurrentAgendaIndex) {
  const session = (yield select(sessionSelectors.selectResourceById, currentAgendaIndex.sessionId)) as ISession;
  yield call(sessionService.storeUpdatedSessionResource, session, {
    currentStep: currentAgendaIndex.index,
    lastStepStartedAt: currentAgendaIndex.lastStepStartedAt,
  });

  yield put(currentAgendaIndex ? actions.storeCurrentAgenda(currentAgendaIndex) : actions.clearCurrentAgenda());
}
